import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Link,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  requestCount: number;
  revoked: boolean;
  createdAt: string;
  scopes?: string[];
  allowedPlatforms?: string[] | null;
}

const formatDate = (stamp: string | null) => {
  if (!stamp) return 'Never';
  const iso = stamp.includes('T') ? stamp : stamp.replace(' ', 'T') + 'Z';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return stamp;
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

export const Settings: React.FC = () => {
  const { token, user } = useAuth();
  const theme = useTheme();
  // Form dialogs get the full screen on a phone; a centred box leaves no room to type.
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  // The plaintext key, available only in the creation response
  const [createdKey, setCreatedKey] = useState<{ name: string; key: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // Notification settings. Kept in the database rather than an env var so recipients can be
  // changed here instead of by an ssh and a rebuild.
  const [notify, setNotify] = useState<{
    notifyTo: string;
    notifyOnScheduled: boolean;
    notifyOnFailure: boolean;
    notifyOnConnection: boolean;
    mailConfigured: boolean;
  } | null>(null);
  const [notifyDraft, setNotifyDraft] = useState('');
  const [notifySaving, setNotifySaving] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifyErr, setNotifyErr] = useState('');
  const [testing, setTesting] = useState(false);

  // Scopes were only settable through the API, so every key made here got the default
  // posts:read + posts:write and could never upload media.
  const [options, setOptions] = useState<{
    scopes: { id: string; description: string }[];
    platforms: string[];
  }>({ scopes: [], platforms: [] });
  const [chosenScopes, setChosenScopes] = useState<string[]>(['posts:read', 'posts:write']);
  const [chosenPlatforms, setChosenPlatforms] = useState<string[]>([]);

  const loadKeys = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/api-keys`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        setKeys(await response.json());
      } else {
        setError('Failed to load API keys');
      }
    } catch (err) {
      setError('Network error loading API keys');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const loadNotify = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/notification-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      setNotify(data);
      // One address per line is easier to edit than a comma-separated string
      setNotifyDraft((data.notifyTo || '').split(/,\s*/).filter(Boolean).join('\n'));
    } catch {
      /* the card simply does not render */
    }
  }, [token]);

  useEffect(() => {
    loadNotify();
  }, [loadNotify]);

  const saveNotify = async (patch: Record<string, unknown>) => {
    setNotifySaving(true);
    setNotifyErr('');
    setNotifyMsg('');
    try {
      const r = await fetch(`${API_URL}/api/notification-settings`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await r.json();
      if (!r.ok) {
        setNotifyErr(data.message || data.error || 'Could not save');
        return;
      }
      setNotify(prev => (prev ? { ...prev, ...data } : prev));
      setNotifyDraft((data.notifyTo || '').split(/,\s*/).filter(Boolean).join('\n'));
      setNotifyMsg('Saved');
    } catch {
      setNotifyErr('Network error saving notification settings');
    } finally {
      setNotifySaving(false);
    }
  };

  const sendTestEmail = async () => {
    setTesting(true);
    setNotifyErr('');
    setNotifyMsg('');
    try {
      const r = await fetch(`${API_URL}/api/mail-test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ send: true }),
      });
      const data = await r.json();
      if (!r.ok || !data.ok) {
        setNotifyErr(data.error || data.hint || 'SMTP is not working');
        return;
      }
      setNotifyMsg(data.sent ? 'Test email sent' : 'Credentials are valid, but the send failed');
    } catch {
      setNotifyErr('Network error sending the test');
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    fetch(`${API_URL}/api/api-keys/options`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => data && setOptions(data))
      .catch(() => { /* the form still works with the defaults */ });
  }, [token]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) {
      setError('Give the key a name so you can tell your keys apart');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newKeyName.trim(),
          scopes: chosenScopes,
          // Empty means every platform; only send a restriction when one is chosen
          allowedPlatforms: chosenPlatforms.length ? chosenPlatforms : undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setCreatedKey({ name: data.name, key: data.key });
        setNewKeyName('');
        setChosenScopes(['posts:read', 'posts:write']);
        setChosenPlatforms([]);
        loadKeys();
      } else {
        setError(data.error || 'Failed to create API key');
      }
    } catch (err) {
      setError('Network error creating API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string, name: string) => {
    if (!window.confirm(`Revoke "${name}"? Any program using this key stops working immediately.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/api-keys/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        loadKeys();
      } else {
        setError('Failed to revoke key');
      }
    } catch (err) {
      setError('Network error revoking key');
    }
  };

  const copyKey = () => {
    if (!createdKey) return;
    navigator.clipboard.writeText(createdKey.key).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => setError('Could not copy — select the key and copy it manually'),
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // The server refuses key creation for demo accounts; say so rather than showing a
  // form that fails
  if (user?.role === 'demo') {
    return (
      <Box>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>Misc Config</Typography>
        <Alert severity="info">
          API key management is not available on the LinkedIn Demo Account.
        </Alert>
      </Box>
    );
  }

  const activeKeys = keys.filter(k => !k.revoked);

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Misc Config
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            API Keys
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Let programs outside ShoutVector publish and schedule posts. A key can post to
            every connected account, so treat it like a password. See the{' '}
            <Link href="/api-documentation">
              API documentation
            </Link>{' '}
            for the endpoints.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <TextField
              size="small"
              label="Key name"
              placeholder="e.g. blog cross-poster"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              sx={{ mb: 2, minWidth: 260 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              What this key may do
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Grant only what the program needs. Uploading images requires media:write.
            </Typography>

            <FormGroup sx={{ mb: 2 }}>
              {options.scopes.map(scope => (
                <FormControlLabel
                  key={scope.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={chosenScopes.includes(scope.id)}
                      onChange={(e) => setChosenScopes(prev =>
                        e.target.checked
                          ? [...prev, scope.id]
                          : prev.filter(x => x !== scope.id)
                      )}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      <Box component="code" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                        {scope.id}
                      </Box>
                      {' — '}{scope.description}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Limit to specific platforms (optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Leave all unchecked to allow every connected platform.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {options.platforms.map(platform => (
                <Chip
                  key={platform}
                  label={platform}
                  size="small"
                  variant={chosenPlatforms.includes(platform) ? 'filled' : 'outlined'}
                  color={chosenPlatforms.includes(platform) ? 'primary' : 'default'}
                  onClick={() => setChosenPlatforms(prev =>
                    prev.includes(platform)
                      ? prev.filter(x => x !== platform)
                      : [...prev, platform]
                  )}
                  sx={{ textTransform: 'capitalize' }}
                />
              ))}
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              disabled={creating || !newKeyName.trim() || chosenScopes.length === 0}
            >
              {creating ? 'Creating…' : 'Create key'}
            </Button>
          </Box>

          {activeKeys.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No API keys yet. Create one above to start posting from outside the app.
            </Typography>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 620 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Scopes</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell align="right">Requests</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {activeKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>{key.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {key.prefix}…
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 260 }}>
                        {(key.scopes || []).map(scope => (
                          <Chip
                            key={scope}
                            label={scope}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem', fontFamily: 'monospace' }}
                          />
                        ))}
                        {key.allowedPlatforms && (
                          <Tooltip title={`Limited to: ${key.allowedPlatforms.join(', ')}`}>
                            <Chip
                              label={key.allowedPlatforms.join('/')}
                              size="small"
                              color="primary"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(key.createdAt)}</TableCell>
                    <TableCell>{formatDate(key.lastUsedAt)}</TableCell>
                    <TableCell align="right">{key.requestCount}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Revoke this key">
                        <IconButton size="small" onClick={() => handleRevoke(key.id, key.name)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          )}

          {keys.some(k => k.revoked) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {keys.filter(k => k.revoked).length} revoked key(s) hidden.
            </Typography>
          )}
        </CardContent>
      </Card>

      {notify && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>Email notifications</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Where ShoutVector sends operational mail. One address per line.
            </Typography>

            {!notify.mailConfigured && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                SMTP is not configured on the server, so nothing will be sent regardless of what
                is set here.
              </Alert>
            )}

            {notifyErr && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setNotifyErr('')}>{notifyErr}</Alert>}
            {notifyMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotifyMsg('')}>{notifyMsg}</Alert>}

            <TextField
              label="Recipients"
              value={notifyDraft}
              onChange={e => setNotifyDraft(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              size="small"
              placeholder={'eng@solbid.com\nnraymond@solbid.com'}
              sx={{ mb: 2 }}
            />

            <FormGroup sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notify.notifyOnScheduled}
                    onChange={e => saveNotify({ notifyOnScheduled: e.target.checked })}
                  />
                }
                label="When a post is scheduled"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notify.notifyOnFailure}
                    onChange={e => saveNotify({ notifyOnFailure: e.target.checked })}
                  />
                }
                label="When a post does not fully go out"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notify.notifyOnConnection}
                    onChange={e => saveNotify({ notifyOnConnection: e.target.checked })}
                  />
                }
                label="When a network connection needs reconnecting, or expires within a week"
              />
            </FormGroup>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                disabled={notifySaving}
                onClick={() => saveNotify({ notifyTo: notifyDraft })}
              >
                Save recipients
              </Button>
              <Button
                size="small"
                disabled={testing || !notify.mailConfigured}
                onClick={sendTestEmail}
              >
                {testing ? 'Sending…' : 'Send test email'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* An alert rather than body text: the caveat about downtime is the part people need
          to notice, and as a plain paragraph at the foot of the page it read as filler. */}
      <Alert severity="info" icon={<ScheduleIcon />} sx={{ mb: 3 }}>
        <AlertTitle>How scheduled posting works</AlertTitle>
        Scheduled posts are published by a scheduler that runs <strong>every 60 seconds</strong>,
        so a post goes out within about a minute of its scheduled time.
        <Box component="span" sx={{ display: 'block', mt: 1 }}>
          It only runs while the server is up. If the server is down when a post comes due, the
          post publishes shortly after startup instead — <strong>late rather than never</strong>.
        </Box>
      </Alert>

      {/* The plaintext key is only available now, so make that unmissable */}
      <Dialog open={Boolean(createdKey)} onClose={() => setCreatedKey(null)} maxWidth="sm" fullWidth fullScreen={narrow}>
        <DialogTitle>API key created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Copy this key now</AlertTitle>
            This is the only time it can be shown. Only a hash is stored, so it cannot be
            recovered later — if you lose it, revoke it and create another.
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {createdKey?.name}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}
            >
              {createdKey?.key}
            </Typography>
            <Tooltip title={copied ? 'Copied' : 'Copy'}>
              <IconButton size="small" onClick={copyKey}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Use it as: Authorization: Bearer {createdKey?.key.slice(0, 16)}…
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedKey(null)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
